/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import Toster from "../../../../common/Toster";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createActivity,
  getActivityById,
  getActivityFAQ,
  getActivityTicketTypePrices,
  getActivityTickets,
  getActivityTermsConditions,
  getActivityTimeSlotPricing,
  updateActivity,
} from "../activityService";
import GeneralInfo from "../AddActivityTabs/GeneralInfo";
import ActivityPricing from "../AddActivityTabs/ActivityPricing";
import ActivityFAQ from "../AddActivityTabs/ActivityFAQ";
import ActivityTerms from "../AddActivityTabs/ActivityTerms";
import { getVisibleBuilderSections } from "./activityBuilderConfig.v2";
import { computeSectionStatuses } from "./activityCompletionRules";
import { buildActivityFormData, hasRequiredMediaForCreate, EMPTY_ACTIVITY_FORM, mapActivityApiToForm } from "./activityFormUtils";
import { ACTIVITY_STAGES } from "../activitySectionUtils";
import {
  getActivityCompletedUnlockOrder,
  getActivityResumeSectionId,
  getNextUnlockOrderAfterSave,
  getSectionUnlockOrder,
  isActivityBuilderSectionAccessible,
} from "./activityWorkflowUtils";
import SectionShell from "./layout/SectionShell";
import ActivityProgressSidebar from "./layout/ActivityProgressSidebar";
import ActivityMobileStepper from "./layout/ActivityMobileStepper";
import SectionTicketTypes from "./sections/SectionTicketTypes";
import SectionAvailability from "./sections/SectionAvailability";
import { CopyFromActivityModal } from "./sections/SectionReviewSubmit";
import { usePackageSectionSpy } from "../../../package/usePackageSectionSpy";
import { useSectionDirtyGuard } from "../../../../../utils/useSectionDirtyGuard";
import { useFormHydrationGuard } from "../../../../../utils/useFormHydrationGuard";
import UnsavedSectionDialog from "../../../../common/UnsavedSectionDialog";
import PackageActionBar from "../../../package/PackageActionBar";

const SCROLL_OFFSET = 168;
const STAGE_MAP = {
  general: ACTIVITY_STAGES.GENERAL,
  tickets: ACTIVITY_STAGES.TICKETS,
  availability: ACTIVITY_STAGES.ITINERARY,
  pricing: ACTIVITY_STAGES.PRICING,
  faq: ACTIVITY_STAGES.FAQ,
  terms: ACTIVITY_STAGES.TERMS,
};

const ActivityBuilder = ({ open, onClose, activityId, isAdmin = false }) => {
  const [activeTab, setActiveTab] = useState("general");
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [validationErrors, setValidationErrors] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isGeneralValid, setIsGeneralValid] = useState(false);
  const [stepValidity, setStepValidity] = useState({});
  const [showToster, setShowToster] = useState(false);
  const [tosterMessage, setTosterMessage] = useState("");
  const [tosterType, setTosterType] = useState("success");
  const [toastKey, setToastKey] = useState(0);
  const [createdActivityId, setCreatedActivityId] = useState(null);
  const [touchedSections, setTouchedSections] = useState({});
  const [enableCancellation, setEnableCancellation] = useState(false);
  const [cancellationPolicies, setCancellationPolicies] = useState([]);
  const [copyModal, setCopyModal] = useState({ open: false, mode: "faq" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savingSectionId, setSavingSectionId] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [generalFormKey, setGeneralFormKey] = useState(0);
  const [activityNameDraft, setActivityNameDraft] = useState("");
  const [savedUnlockOrder, setSavedUnlockOrder] = useState(1);
  const [formFlags, setFormFlags] = useState({ free_booking: 0, time_slot_based: 0 });
  const generalFormDataRef = useRef({ ...EMPTY_ACTIVITY_FORM });
  const saveBeforeLeaveRef = useRef(null);
  const saveHandlersRef = useRef({});
  const contentRef = useRef(null);
  const sectionRefs = useRef({});
  const isScrollingRef = useRef(false);
  const pinnedSectionRef = useRef(null);
  const manualSectionRef = useRef(null);
  const editingSectionRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const userNavigatedRef = useRef(false);
  const resumedRef = useRef(false);
  const scrollSyncTimersRef = useRef([]);
  const resizeObserverRef = useRef(null);
  const hydratedActivityIdRef = useRef(null);
  const builderSessionRef = useRef(null);

  const currentId = activityId || createdActivityId;
  const isEditingExisting = Boolean(activityId);
  const queryClient = useQueryClient();
  const { markEdited: markFormEdited, markHydrated, resetHydration } =
    useFormHydrationGuard(currentId);

  const { data: activityData } = useQuery({
    queryKey: ["activity", currentId],
    queryFn: () => getActivityById(currentId),
    enabled: open && !!currentId,
  });

  const isFreeBooking =
    Number(formFlags.free_booking) === 1 ||
    Number(activityData?.data?.free_booking) === 1;

  const timeSlotBased =
    Number(formFlags.time_slot_based) === 1 ||
    Number(activityData?.data?.time_slot_based) === 1;

  const visibleSections = getVisibleBuilderSections(isFreeBooking);
  const sectionIds = useMemo(() => visibleSections.map((s) => s.id), [isFreeBooking]);

  const { data: ticketsData } = useQuery({
    queryKey: ["activityTickets", currentId],
    queryFn: () => getActivityTickets(currentId),
    enabled: open && !!currentId,
  });

  const { data: pricingData } = useQuery({
    queryKey: ["activityTicketTypePrices", currentId],
    queryFn: () => getActivityTicketTypePrices(currentId),
    enabled: open && !!currentId,
  });

  const { data: faqData } = useQuery({
    queryKey: ["activityFAQ", currentId],
    queryFn: () => getActivityFAQ(currentId),
    enabled: open && !!currentId,
  });

  const { data: termsData } = useQuery({
    queryKey: ["activityTerms", currentId],
    queryFn: () => getActivityTermsConditions(currentId),
    enabled: open && !!currentId,
  });

  const { data: slotData } = useQuery({
    queryKey: ["getActivityTimeSlotPricing", currentId],
    queryFn: () => getActivityTimeSlotPricing(currentId),
    enabled: open && !!currentId && timeSlotBased,
  });

  const tickets = ticketsData?.data || [];
  const prices = pricingData?.data || [];
  const faqs = faqData?.data || [];
  const timeSlots = slotData?.data || [];

  const activityTermsList = useMemo(() => {
    const raw = termsData?.data;
    if (Array.isArray(raw?.terms_and_conditions)) return raw.terms_and_conditions;
    if (Array.isArray(raw)) return raw;
    return raw ? [raw] : [];
  }, [termsData]);

  const hasTerms = activityTermsList.some((t) => String(t?.terms_and_conditions || "").trim());
  const faqComplete =
    Boolean(stepValidity.faq) ||
    (Array.isArray(faqs) && faqs.some((f) => f.question && f.answer));

  const hasThumb = Boolean(
    generalFormDataRef.current?.thumb_image || activityData?.data?.thumb_image
  );
  const hasCover = Boolean(
    generalFormDataRef.current?.cover_image || activityData?.data?.cover_image
  );

  const completedUnlockOrder = getActivityCompletedUnlockOrder(
    savedUnlockOrder ?? activityData?.data?.current_stage,
    isFreeBooking
  );

  const handleGeneralFormChange = useCallback((data) => {
    generalFormDataRef.current = data;
    setActivityNameDraft(data?.name || "");
    setFormFlags({
      free_booking: data?.free_booking ?? 0,
      time_slot_based: data?.time_slot_based ?? 0,
    });
  }, []);

  const sectionStatuses = computeSectionStatuses({
    completedUnlockOrder,
    visibleSections,
    touchedSections,
  });

  const showToast = (type, message) => {
    setTosterType(type);
    setTosterMessage(message);
    setToastKey((k) => k + 1);
    setShowToster(true);
  };

  const createMutation = useMutation({
    mutationFn: createActivity,
    onSuccess: (data) => {
      setCreatedActivityId(data.data.id);
      const nextStage = getNextUnlockOrderAfterSave("general", isFreeBooking);
      generalFormDataRef.current = {
        ...generalFormDataRef.current,
        current_stage: nextStage,
      };
      setSavedUnlockOrder(nextStage);
      queryClient.invalidateQueries(["activities"]);
    },
    onError: (error) => {
      setValidationErrors(error.response?.data?.errors || {});
      showToast("error", error.response?.data?.message || "Failed to create activity");
      throw error;
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateActivity(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["activities"]);
      queryClient.invalidateQueries(["activity", currentId]);
    },
    onError: (error) => {
      setValidationErrors(error.response?.data?.errors || {});
      showToast("error", error.response?.data?.message || "Failed to update activity");
      throw error;
    },
  });

  const persistActivity = useCallback(
    async (data, { stage } = {}) => {
      const hadGalleryUploads =
        Array.isArray(data?.gallery_images) &&
        data.gallery_images.some((file) => file instanceof File);
      const hadMediaUploads =
        hadGalleryUploads ||
        data?.thumb_image instanceof File ||
        data?.cover_image instanceof File;

      const fd = buildActivityFormData(data, {
        currentStage: stage ?? STAGE_MAP[activeTab] ?? ACTIVITY_STAGES.GENERAL,
      });
      if (currentId) {
        await updateMutation.mutateAsync({ id: currentId, data: fd });
        if (hadMediaUploads) {
          const fresh = await queryClient.fetchQuery({
            queryKey: ["activity", currentId],
            queryFn: () => getActivityById(currentId),
          });
          generalFormDataRef.current = mapActivityApiToForm(
            fresh?.data,
            generalFormDataRef.current,
            { preferServerMedia: true }
          );
          setGeneralFormKey((key) => key + 1);
        }
        return;
      }
      fd.set(
        "current_stage",
        String(stage ?? getNextUnlockOrderAfterSave("general", isFreeBooking))
      );
      await createMutation.mutateAsync(fd);
    },
    [currentId, activeTab, isFreeBooking, createMutation, updateMutation, queryClient]
  );

  const clearScrollSyncTimers = () => {
    scrollSyncTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    scrollSyncTimersRef.current = [];
  };

  const disconnectSectionResizeObserver = () => {
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
  };

  const scrollSectionToTop = (sectionId, behavior = "auto") => {
    const section = sectionRefs.current[sectionId];
    if (!section) return;

    const top =
      section.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
    window.scrollTo({ top: Math.max(0, top), behavior });
  };

  const handleSpyActiveChange = useCallback((sectionId) => {
    if (manualSectionRef.current) return;
    if (editingSectionRef.current) return;
    if (isScrollingRef.current || pinnedSectionRef.current) return;
    setActiveTab(sectionId);
  }, []);

  useEffect(() => {
    editingSectionRef.current = editingSectionId;
  }, [editingSectionId]);

  useEffect(() => {
    isSubmittingRef.current = isSubmitting || savingSectionId !== null;
  }, [isSubmitting, savingSectionId]);

  const displaySectionId = editingSectionId || activeTab;

  const scrollToSection = (sectionId, { userInitiated = false } = {}) => {
    if (userInitiated) {
      userNavigatedRef.current = true;
      manualSectionRef.current = sectionId;
      clearScrollSyncTimers();
      disconnectSectionResizeObserver();
    }

    setActiveTab(sectionId);
    setEditingSectionId(null);
    editingSectionRef.current = null;
    setTouchedSections((p) => ({ ...p, [sectionId]: true }));
    pinnedSectionRef.current = sectionId;

    const section = sectionRefs.current[sectionId];
    if (section) {
      isScrollingRef.current = true;
      const behavior = userInitiated ? "auto" : "smooth";

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollSectionToTop(sectionId, behavior);
        });
      });

      const scrollLockMs = userInitiated ? 700 : 1000;
      window.setTimeout(() => {
        isScrollingRef.current = false;
        if (manualSectionRef.current === sectionId || !userInitiated) {
          setActiveTab(sectionId);
        }
        window.setTimeout(() => {
          if (pinnedSectionRef.current === sectionId) {
            pinnedSectionRef.current = null;
          }
          if (userInitiated && manualSectionRef.current === sectionId) {
            manualSectionRef.current = null;
          }
        }, userInitiated ? 400 : 200);
      }, scrollLockMs);
    }
  };

  const {
    leaveDialogOpen,
    requestLeave,
    cancelLeave,
    confirmLeaveSave,
    clearSectionDirty,
    getSectionCaptureProps,
  } = useSectionDirtyGuard({
    activeSectionId: displaySectionId,
    isLoading: isSubmitting,
    getSaveBeforeLeave: () => saveBeforeLeaveRef.current,
  });

  const activeSectionLabel =
    visibleSections.find((section) => section.id === displaySectionId)?.name || "Section";

  const isActiveSectionDisabled =
    (!currentId && displaySectionId !== "general") ||
    (!isEditingExisting &&
      !isActivityBuilderSectionAccessible(
        getSectionUnlockOrder(displaySectionId, isFreeBooking),
        completedUnlockOrder,
        Boolean(currentId),
        isEditingExisting
      ) &&
      displaySectionId !== "general");

  const handleSectionNavClick = (sectionId, unlockOrder) => {
    if (!currentId && sectionId !== "general") {
      showToast("error", "Save General Information first.");
      return;
    }

    if (
      !isEditingExisting &&
      !isActivityBuilderSectionAccessible(
        unlockOrder,
        completedUnlockOrder,
        Boolean(currentId),
        isEditingExisting
      ) &&
      sectionId !== "general"
    ) {
      showToast("error", "Save the current section first to unlock the next step.");
      return;
    }

    scrollToSection(sectionId, { userInitiated: true });
  };

  const handleLeaveBuilder = () => {
    requestLeave(onClose);
  };

  useEffect(() => {
    if (!open) {
      builderSessionRef.current = null;
      hydratedActivityIdRef.current = null;
      resumedRef.current = false;
      userNavigatedRef.current = false;
      return;
    }

    const sessionKey = activityId ?? "new";
    if (builderSessionRef.current === sessionKey) return;
    builderSessionRef.current = sessionKey;

    setCreatedActivityId(null);
    generalFormDataRef.current = { ...EMPTY_ACTIVITY_FORM };
    setActivityNameDraft("");
    setSavedUnlockOrder(1);
    setFormFlags({ free_booking: 0, time_slot_based: 0 });
    setGeneralFormKey((key) => key + 1);
    resetHydration();
    hydratedActivityIdRef.current = null;
    setValidationErrors(null);
    setActiveTab("general");
    setEditingSectionId(null);
    editingSectionRef.current = null;
    setTouchedSections({});
    setLastSavedAt(null);
    resumedRef.current = false;
    userNavigatedRef.current = false;
    manualSectionRef.current = null;
    pinnedSectionRef.current = null;
    clearScrollSyncTimers();
    disconnectSectionResizeObserver();
  }, [open, activityId, resetHydration]);

  useEffect(() => {
    if (!open || !currentId || !activityData?.data) return;
    if (hydratedActivityIdRef.current === currentId) return;

    markHydrated(currentId);
    hydratedActivityIdRef.current = currentId;
    const mapped = mapActivityApiToForm(activityData.data, { ...EMPTY_ACTIVITY_FORM });
    const stage = activityData.data.current_stage || ACTIVITY_STAGES.GENERAL;
    const mappedUnlockOrder = getActivityCompletedUnlockOrder(
      stage,
      Number(activityData.data.free_booking) === 1
    );
    generalFormDataRef.current = {
      ...mapped,
      current_stage: mappedUnlockOrder,
    };
    setSavedUnlockOrder(mappedUnlockOrder);
    setActivityNameDraft(mapped.name || "");
    setFormFlags({
      free_booking: mapped.free_booking ?? 0,
      time_slot_based: mapped.time_slot_based ?? 0,
    });
    setGeneralFormKey((key) => key + 1);
  }, [open, currentId, activityData?.data, markHydrated]);

  useEffect(() => {
    if (!open || !activityId || !activityData?.data) return;
    if (resumedRef.current || userNavigatedRef.current) return;
    resumedRef.current = true;
    const resume = getActivityResumeSectionId(
      activityData.data.current_stage,
      Number(activityData.data.free_booking) === 1
    );
    window.setTimeout(() => {
      if (userNavigatedRef.current) return;
      scrollToSection(resume);
    }, 150);
  }, [open, activityId, activityData?.data]);

  useEffect(() => {
    if (!open) return undefined;

    const isEditableTarget = (target) => {
      if (!(target instanceof HTMLElement)) return false;
      return Boolean(
        target.closest(
          "input, select, textarea, [contenteditable='true'], [role='listbox'], [data-prevent-section-spy='true']"
        )
      );
    };

    const releaseManualSection = (event) => {
      if (isScrollingRef.current || pinnedSectionRef.current) return;
      if (event?.target instanceof HTMLElement) {
        if (event.target.closest("[data-modal-overlay='true']")) return;
        if (event.target.closest("[data-prevent-section-scroll='true']")) return;
        if (isEditableTarget(event.target)) return;
      }
      if (isEditableTarget(document.activeElement)) return;
      manualSectionRef.current = null;
      editingSectionRef.current = null;
      setEditingSectionId(null);
    };

    const handleFocusIn = (event) => {
      if (!contentRef.current?.contains(event.target)) return;
      const sectionEl = event.target?.closest?.("[data-section-id]");
      const sectionId = sectionEl?.dataset?.sectionId;
      if (!sectionId) return;
      manualSectionRef.current = sectionId;
      editingSectionRef.current = sectionId;
      queueMicrotask(() => {
        setEditingSectionId(sectionId);
      });
      // Do not call setActiveTab here — it re-renders the builder and drops the first keystroke.
    };

    const handleContentPointerDown = (event) => {
      if (!contentRef.current?.contains(event.target)) return;

      if (
        event.target instanceof HTMLElement &&
        event.target.closest(
          "button, [role='button'], [data-prevent-section-scroll='true']"
        )
      ) {
        clearScrollSyncTimers();
        disconnectSectionResizeObserver();
        pinnedSectionRef.current = null;
        isScrollingRef.current = false;
      }
    };

    const handleKeyDown = (event) => {
      if (
        ["PageUp", "PageDown", "ArrowUp", "ArrowDown", "Home", "End", " "].includes(
          event.key
        )
      ) {
        if (!isEditableTarget(event.target)) {
          releaseManualSection();
        }
      }
    };

    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("pointerdown", handleContentPointerDown, true);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("pointerdown", handleContentPointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  usePackageSectionSpy({
    sectionIds,
    sectionRefs,
    isScrollingRef,
    pinnedSectionRef,
    editingSectionRef,
    isSubmittingRef,
    onActiveChange: handleSpyActiveChange,
    offset: SCROLL_OFFSET,
    enabled: open,
  });

  const registerStepSave = (tabId) => (fn) => {
    if (typeof fn === "function") saveHandlersRef.current[tabId] = fn;
  };

  const registerStepValidity = (tabId) => (isValid) => {
    setStepValidity((prev) => ({ ...prev, [tabId]: Boolean(isValid) }));
  };

  const advanceStage = async (sectionId) => {
    if (!currentId) return;
    const nextUnlockOrder = getNextUnlockOrderAfterSave(sectionId, isFreeBooking);
    const highestCompleted = Math.max(Number(completedUnlockOrder) || 1, nextUnlockOrder);
    const fd = new FormData();
    fd.append("current_stage", String(highestCompleted));
    await updateActivity(currentId, fd);
    setSavedUnlockOrder(highestCompleted);
    generalFormDataRef.current = {
      ...generalFormDataRef.current,
      current_stage: highestCompleted,
    };
    queryClient.invalidateQueries(["activity", currentId]);
  };

  const goToNextSection = (currentSectionId) => {
    const idx = visibleSections.findIndex((section) => section.id === currentSectionId);
    if (idx >= 0 && idx < visibleSections.length - 1) {
      scrollToSection(visibleSections[idx + 1].id, { userInitiated: true });
    }
  };

  const handleSave = async (sectionId = displaySectionId, { skipAutoAdvance = false } = {}) => {
    setSavingSectionId(sectionId);
    setIsSubmitting(true);
    if (sectionId === "general") {
      setSubmitAttempted(true);
    }
    setTouchedSections((prev) => ({ ...prev, [sectionId]: true }));

    const sectionName =
      visibleSections.find((section) => section.id === sectionId)?.name || "Section";

    try {
      switch (sectionId) {
        case "general": {
          const generalFormData = generalFormDataRef.current;
          if (!isGeneralValid) {
            showToast(
              "error",
              "Please complete all required fields including cover and thumbnail"
            );
            return false;
          }
          if (!hasRequiredMediaForCreate(generalFormData)) {
            showToast("error", "Cover image and thumbnail are required");
            return false;
          }
          const nextUnlockOrder = getNextUnlockOrderAfterSave("general", isFreeBooking);
          const stageToSave = Math.max(
            Number(completedUnlockOrder) || 1,
            nextUnlockOrder
          );
          await persistActivity(generalFormData, { stage: stageToSave });
          generalFormDataRef.current = {
            ...generalFormDataRef.current,
            current_stage: stageToSave,
          };
          setSavedUnlockOrder(stageToSave);
          setLastSavedAt(new Date());
          clearSectionDirty("general");
          if (!skipAutoAdvance) goToNextSection("general");
          showToast("success", "General Information saved successfully");
          break;
        }
        case "tickets": {
          if (!currentId) {
            showToast("error", "Save General Information first");
            return false;
          }
          if (tickets.length === 0) {
            showToast("error", "Please add at least one ticket type before continuing");
            return false;
          }
          await advanceStage("tickets");
          setLastSavedAt(new Date());
          clearSectionDirty("tickets");
          if (!skipAutoAdvance) goToNextSection("tickets");
          showToast("success", "Ticket Types saved successfully");
          break;
        }
        case "availability":
        case "pricing":
        case "faq": {
          if (!currentId) {
            showToast("error", "Save General Information first");
            return false;
          }
          const handler = saveHandlersRef.current[sectionId];
          if (!handler) {
            showToast("error", `Nothing to save in ${sectionName}`);
            return false;
          }
          await handler();
          if (sectionId === "availability" && enableCancellation) {
            const cancelHandler = saveHandlersRef.current.cancellation;
            if (cancelHandler) await cancelHandler();
          }
          await advanceStage(sectionId);
          setLastSavedAt(new Date());
          clearSectionDirty(sectionId);
          if (!skipAutoAdvance) goToNextSection(sectionId);
          showToast("success", `${sectionName} saved successfully`);
          break;
        }
        case "terms": {
          if (!currentId) {
            showToast("error", "Save General Information first");
            return false;
          }
          const termsHandler = saveHandlersRef.current.terms;
          if (!termsHandler) {
            showToast("error", `Nothing to save in ${sectionName}`);
            return false;
          }
          await termsHandler();
          if (skipAutoAdvance) {
            setLastSavedAt(new Date());
            clearSectionDirty("terms");
            showToast("success", "Terms saved successfully");
            break;
          }
          const completedStage = getNextUnlockOrderAfterSave("terms", isFreeBooking);
          const fd = new FormData();
          fd.append("current_stage", String(completedStage));
          if (!isAdmin) {
            fd.append("submit_for_approval", "1");
          }
          await updateActivity(currentId, fd);
          setSavedUnlockOrder(completedStage);
          generalFormDataRef.current = {
            ...generalFormDataRef.current,
            current_stage: completedStage,
          };
          queryClient.invalidateQueries(["activities"]);
          queryClient.invalidateQueries(["supplierActivities"]);
          queryClient.invalidateQueries(["activity", currentId]);
          setLastSavedAt(new Date());
          clearSectionDirty("terms");
          showToast(
            "success",
            isAdmin
              ? "Activity saved successfully!"
              : "Activity submitted for review!"
          );
          if (!skipAutoAdvance) {
            setTimeout(onClose, isAdmin ? 800 : 1500);
          }
          break;
        }
        default:
          break;
      }
      return true;
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message ||
          error?.message ||
          `Failed to save ${sectionName}`
      );
      return false;
    } finally {
      setIsSubmitting(false);
      setSavingSectionId(null);
    }
  };

  saveBeforeLeaveRef.current = () => handleSave(displaySectionId, { skipAutoAdvance: true });

  const renderContent = (item) => {
    switch (item.id) {
      case "general":
        return (
          <GeneralInfo
            key={`activity-general-${generalFormKey}`}
            variant="all"
            formDataRef={generalFormDataRef}
            onFormChange={handleGeneralFormChange}
            skipServerHydration
            onUserEdit={markFormEdited}
            activityId={currentId}
            validationErrors={validationErrors}
            submitAttempted={submitAttempted}
            onValidityChange={setIsGeneralValid}
          />
        );
      case "tickets":
        return <SectionTicketTypes activityId={currentId} />;
      case "availability":
        return (
          <SectionAvailability
            activityId={currentId}
            registerSave={registerStepSave("availability")}
            registerCancellationSave={registerStepSave("cancellation")}
            cancellationPolicies={cancellationPolicies}
            setCancellationPolicies={setCancellationPolicies}
            enableCancellation={enableCancellation}
            setEnableCancellation={setEnableCancellation}
          />
        );
      case "pricing":
        return (
          <ActivityPricing
            activityId={currentId}
            registerSave={registerStepSave("pricing")}
            onValidityChange={registerStepValidity("pricing")}
            hideCloseOutSection
            hideSlotManagement
          />
        );
      case "faq":
        return (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setCopyModal({ open: true, mode: "faq" })}
              className="text-xs font-semibold text-primary hover:underline"
            >
              Use FAQs from previous activity
            </button>
            <ActivityFAQ
              activityId={currentId}
              registerSave={registerStepSave("faq")}
              onValidityChange={registerStepValidity("faq")}
            />
          </div>
        );
      case "terms":
        return (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setCopyModal({ open: true, mode: "terms" })}
              className="text-xs font-semibold text-primary hover:underline"
            >
              Use terms from previous activity
            </button>
            <ActivityTerms
              activityId={currentId}
              registerSave={registerStepSave("terms")}
              onValidityChange={registerStepValidity("terms")}
            />
          </div>
        );
      default:
        return null;
    }
  };

  if (!open) return null;

  const activityTitle =
    activityNameDraft || activityData?.data?.name || "New Activity";

  return (
    <div className="min-h-full bg-slate-50">
      <div className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex w-full items-center justify-between gap-3 px-4 py-2.5 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={handleLeaveBuilder}
              className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <i className="fi fi-rr-arrow-left" />
              <span className="hidden sm:inline">Back</span>
            </button>
            <h1 className="truncate text-base font-bold text-gray-900 sm:text-lg">{activityTitle}</h1>
          </div>
        </div>
        <div className="border-t border-gray-100 px-4 py-2 lg:hidden">
          <ActivityMobileStepper
            sections={visibleSections}
            sectionStatuses={sectionStatuses}
            activeSectionId={displaySectionId}
            hasActivityId={!!currentId}
            completedUnlockOrder={completedUnlockOrder}
            isResumeEdit={isEditingExisting}
            onSectionClick={handleSectionNavClick}
          />
        </div>
      </div>

      <div className="mx-auto flex w-full gap-5 px-4 py-4 sm:px-6 lg:py-5">
        <aside className="relative z-40 hidden w-64 shrink-0 lg:block">
          <div className="sticky top-20 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
            <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Progress
            </p>
            <ActivityProgressSidebar
              sections={visibleSections}
              sectionStatuses={sectionStatuses}
              activeSectionId={displaySectionId}
              hasActivityId={!!currentId}
              completedUnlockOrder={completedUnlockOrder}
              isResumeEdit={isEditingExisting}
              onSectionClick={handleSectionNavClick}
            />
          </div>
        </aside>

        <main ref={contentRef} className="min-w-0 flex-1 space-y-6 pb-24">
          {visibleSections.map((item) => {
            const isDisabledByNoId = !currentId && item.id !== "general";
            const isDisabledByStage =
              !isEditingExisting &&
              !isActivityBuilderSectionAccessible(
                item.unlockOrder,
                completedUnlockOrder,
                Boolean(currentId),
                isEditingExisting
              ) &&
              item.id !== "general";
            const disabled = isDisabledByNoId || isDisabledByStage;
            const status = sectionStatuses[item.id];
            return (
              <SectionShell
                key={item.id}
                sectionId={item.id}
                title={item.name}
                sectionStatus={status}
                isActive={displaySectionId === item.id}
                isDisabled={disabled}
                disabledMessage={
                  disabled
                    ? isDisabledByNoId
                      ? "Save General Information first to unlock this section."
                      : "Save the previous section to unlock this step."
                    : null
                }
                sectionRef={(el) => {
                  if (el) sectionRefs.current[item.id] = el;
                  else delete sectionRefs.current[item.id];
                }}
                onHeaderClick={() => {
                  if (disabled) {
                    showToast(
                      "error",
                      isDisabledByNoId
                        ? "Save General Information first."
                        : "Save the previous section first to unlock this step."
                    );
                    return;
                  }
                  scrollToSection(item.id, { userInitiated: true });
                }}
                contentProps={getSectionCaptureProps(item.id)}
              >
                {!disabled ? (
                  renderContent(item)
                ) : (
                  <p className="text-sm text-gray-500">
                    Complete and save the previous section to access this step.
                  </p>
                )}
              </SectionShell>
            );
          })}
        </main>
      </div>

      <PackageActionBar
        activeSectionLabel={activeSectionLabel}
        isSubmitting={isSubmitting || savingSectionId !== null}
        isDisabled={isActiveSectionDisabled}
        isLastSection={displaySectionId === "terms"}
        lastSavedAt={lastSavedAt}
        onSave={() => handleSave(displaySectionId)}
      />

      <CopyFromActivityModal
        open={copyModal.open}
        mode={copyModal.mode}
        excludeActivityId={currentId}
        onClose={() => setCopyModal({ open: false, mode: "faq" })}
        onSelectFAQ={() => {
          queryClient.invalidateQueries(["activityFAQ", currentId]);
          showToast("success", "FAQs copied — save the FAQ section to apply.");
        }}
        onSelectTerms={() => {
          queryClient.invalidateQueries(["activityTerms", currentId]);
          showToast("success", "Terms copied — save the Terms section to apply.");
        }}
      />

      <UnsavedSectionDialog
        open={leaveDialogOpen}
        sectionLabel={activeSectionLabel}
        onStay={cancelLeave}
        onSaveAndContinue={confirmLeaveSave}
        isSaving={isSubmitting || savingSectionId !== null}
        showSaveButton
        leaveMode
      />

      {showToster && (
        <Toster key={toastKey} message={tosterMessage} type={tosterType} onClose={() => setShowToster(false)} />
      )}
    </div>
  );
};

export default ActivityBuilder;
